// Recovery for scans that arrive with the wrong letter case.
//
// A hardware scanner's keyboard wedge sends SHIFT as a separate HID report
// from the characters it modifies. When the modifier lags the characters —
// a wireless link, a power-managed one, a wedge with no inter-character
// delay — the first few letters of the burst come through unshifted and the
// rest arrive correctly:
//
//     X0013YYHOZ  ->  x0013YYHOZ
//     B0DN4P6NZP  ->  b0dn4P6NZP
//     PEMBOX10    ->  pembOX10
//
// Measured at Pembroke 2026-08-20: one operator lost the shift on 1 scan in
// 4 by mid-afternoon (2% in the morning — the rate tracks the device's
// battery, so a morning bench test passes), the shift landing at the 5th or
// 6th character in 82% of cases. It survived a scanner swap. Barcodes
// compare case-sensitively everywhere in Portal, so every one of those was
// a hard error the operator cleared by scanning again.
//
// WHY THIS IS NOT "fold case on barcode lookups". Barcode comparisons must
// stay case-sensitive — at least one supplier ships barcodes where case is
// significant, and folding it in a catalogue or DB lookup would collapse
// genuinely distinct items. This helper never widens a lookup: it runs only
// AFTER an exact match has already failed, only against a CLOSED set the
// screen is already holding in memory (this order's lines, this warehouse's
// boxes, this wave's items), and it refuses as soon as two candidates in
// that set differ only by case. So a case-significant pair can never be
// silently conflated — it lands in the `ambiguous` branch and the scan is
// still refused, exactly as before.

import { recordScanAnomaly } from "../../api/scanTelemetry";

export interface ScanCaseResult {
    /** The barcode to match with: the candidate when one was recovered,
     *  otherwise the scanned value unchanged. */
    value: string;
    /** True when `value` differs from the scan by case alone. */
    recovered: boolean;
    /** True when two or more candidates matched case-insensitively, so
     *  nothing could be recovered safely. */
    ambiguous: boolean;
}

const asIs = (value: string): ScanCaseResult =>
    ({ value, recovered: false, ambiguous: false });

/**
 * Resolve `scanned` against a closed set of known-good barcodes.
 *
 * Returns the scan unchanged if it matches exactly, if nothing matches, or
 * if the case-insensitive match is ambiguous. Only an unambiguous
 * case-only match is substituted.
 */
export const recoverScanCase = (
    scanned: string,
    candidates: Iterable<string | null | undefined>,
): ScanCaseResult => {
    if (!scanned) return asIs(scanned);

    const lower = scanned.toLowerCase();
    // Distinct, because a candidate list built from order lines or a
    // barcode map repeats the same value freely — three units of one SKU
    // are not three competing candidates.
    const matches = new Set<string>();
    for (const c of candidates) {
        if (!c) continue;
        if (c === scanned) return asIs(scanned);      // exact wins outright
        if (c.toLowerCase() === lower) matches.add(c);
    }

    if (matches.size === 1) {
        return { value: [...matches][0], recovered: true, ambiguous: false };
    }
    // 0 matches: not a case problem, it's an unknown barcode.
    // 2+: genuinely case-significant values — refuse, don't guess.
    return { value: scanned, recovered: false, ambiguous: matches.size > 1 };
};

/**
 * `recoverScanCase` plus the telemetry that makes a failing device
 * attributable. Report every recovery: the operator sees a working scan, so
 * this is the ONLY trace that their scanner is degrading.
 */
export const recoverScanCaseReported = (
    scanned: string,
    candidates: Iterable<string | null | undefined>,
    module: string,
): ScanCaseResult => {
    const result = recoverScanCase(scanned, candidates);
    if (result.recovered) {
        recordScanAnomaly("case_recovered", module,
            { scanned, resolved: result.value, lost_shift: countLostShift(scanned, result.value) },
            { barcode: result.value });
    } else if (result.ambiguous) {
        recordScanAnomaly("case_ambiguous", module, { scanned }, { barcode: scanned });
    }
    return result;
};

// How many leading letters lost their shift. The distribution of this
// across a device's scans is what separates a wedge-timing fault (a tight
// cluster) from an operator typing by hand (scattered).
export const countLostShift = (scanned: string, resolved: string): number => {
    let n = 0;
    for (let i = 0; i < scanned.length && i < resolved.length; i++) {
        if (scanned[i] !== resolved[i]) n++;
    }
    return n;
};
